/**
 * The portal's app roles and how a person's roles are named on screen (AUDIT-READ-1, BUG-AO-01).
 *
 * No imports: the Sidebar, Settings and the server tests read the same rule.
 */

/**
 * RBAC Role Constants
 * These must match the app roles defined in the Entra ID app registration.
 * See Todd's Infrastructure Guide, Section 03.
 */
export const AppRoles = {
  ADMIN: 'Admin',
  OPERATOR: 'Operator',
  VIEWER: 'Viewer',
  /** The account owner: the only reader of the Audit Log (AUDIT-READ-1). Admin does not imply it. */
  ACCOUNT_OWNER: 'AccountOwner',
} as const;

export type AppRole = (typeof AppRoles)[keyof typeof AppRoles];

/** How the account-owner role is named on screen. */
export const ACCOUNT_OWNER_LABEL = 'Account owner';
/** What a person with no portal role is shown. */
export const NO_ROLE_LABEL = 'No Role';
/** The portal's roles, in words, for Settings. */
export const RBAC_ROLES_SUMMARY = 'Admin, Operator, Viewer; Account owner (audit log)';

/**
 * The role a person's badge names: the working role (Admin, Operator or Viewer) when they hold one, the account owner
 * when that is all they hold, otherwise "No Role". The account-owner role is shown beside it where there is room.
 */
export function roleBadgeLabel(roles: readonly string[] | undefined): string {
  const held = roles ?? [];
  const working = held.find((role) => role !== AppRoles.ACCOUNT_OWNER);
  if (working) return working;
  return held.includes(AppRoles.ACCOUNT_OWNER) ? ACCOUNT_OWNER_LABEL : NO_ROLE_LABEL;
}
